/* eslint-disable react/no-unknown-property */
import { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Sparkles, useTexture, Environment, Lightformer } from "@react-three/drei";
import { images } from "../content/site";

/* Photoreal soapy-drum image we fly toward (the bright warm door is its centre) */
function DrumPlane() {
  const tex = useTexture(images.heroDrum);
  const aspect = tex.image ? tex.image.width / tex.image.height : 1672 / 941;
  const h = 30;
  const w = h * aspect;
  return (
    <mesh position={[0, 0, -9]}>
      <planeGeometry args={[w, h]} />
      <meshBasicMaterial map={tex} toneMapped={false} />
    </mesh>
  );
}

/* real 3D soap bubbles drifting toward the camera */
function Bubbles({ count = 18 }) {
  const refs = useRef([]);
  const rand = (a, b) => a + Math.random() * (b - a);
  const data = useMemo(
    () =>
      Array.from({ length: count }).map(() => ({
        pos: [rand(-8, 8), rand(-5, 5), rand(-9, 4)],
        r: rand(0.14, 0.5),
        speed: rand(0.5, 1.4),
        drift: rand(-0.25, 0.35),
        wob: rand(0, Math.PI * 2),
      })),
    [count]
  );
  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    data.forEach((d, i) => {
      const m = refs.current[i];
      if (!m) return;
      d.pos[2] += delta * d.speed;
      d.pos[1] += delta * d.drift;
      if (d.pos[2] > 5) {
        d.pos[2] = -9;
        d.pos[0] = rand(-8, 8);
        d.pos[1] = rand(-5, 5);
      }
      m.position.set(d.pos[0] + Math.sin(t + d.wob) * 0.15, d.pos[1], d.pos[2]);
      m.rotation.y += delta * 0.3;
    });
  });
  return data.map((d, i) => (
    <mesh key={i} ref={(el) => (refs.current[i] = el)} scale={d.r} position={d.pos}>
      <sphereGeometry args={[1, 32, 32]} />
      <meshPhysicalMaterial
        transmission={1}
        thickness={0.18}
        roughness={0}
        ior={1.1}
        iridescence={1}
        iridescenceIOR={1.3}
        iridescenceThicknessRange={[200, 600]}
        transparent
        opacity={0.45}
        color="#ffffff"
        attenuationColor="#ffffff"
        metalness={0}
        clearcoat={1}
        clearcoatRoughness={0}
        specularIntensity={1}
        envMapIntensity={2.6}
      />
    </mesh>
  ));
}

function Rig({ progress }) {
  useFrame((state) => {
    const raw = progress ? Math.max(0, Math.min(1, progress.get())) : 0;
    // complete the fly-through earlier, leaving room for a long tension hold
    const p = Math.min(1, raw / 0.28);
    // fly forward toward the bright door then push through it
    const z = 5 - p * 11.5;
    state.camera.position.z += (z - state.camera.position.z) * 0.12;
    state.camera.position.x = Math.sin(p * Math.PI * 1.6) * 0.5;
    state.camera.position.y = Math.cos(p * Math.PI * 1.2) * 0.32;
    state.camera.lookAt(0, 0, -9);
  });
  return null;
}

export function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext &&
      (c.getContext("webgl") || c.getContext("experimental-webgl"))
    );
  } catch (e) {
    return false;
  }
}

export default function Hero3D({ progress }) {
  return (
    <Canvas
      dpr={[1, 1.7]}
      gl={{ antialias: true, powerPreference: "high-performance" }}
      camera={{ position: [0, 0, 5], fov: 66 }}
    >
      {/* bright, warm, airy — never dark */}
      <color attach="background" args={["#f2ece0"]} />
      <ambientLight intensity={1.15} color="#fff4e2" />
      <pointLight position={[0, 0, -8]} intensity={40} distance={26} color="#ffe6bf" />
      <pointLight position={[2.5, 2, 4]} intensity={22} distance={18} color="#ffffff" />
      <directionalLight position={[4, 6, 6]} intensity={1.4} color="#ffffff" />
      <directionalLight position={[-5, -3, 4]} intensity={0.6} color="#cfe0ff" />

      {/* offline-safe reflection environment so bubbles read as real, bright soap film */}
      <Environment frames={1} resolution={64}>
        <Lightformer intensity={2.4} position={[0, 0, 6]} scale={[14, 14, 1]} color="#fff2df" />
        <Lightformer intensity={1.6} position={[-6, 3, 3]} scale={[9, 9, 1]} color="#e7efff" />
        <Lightformer intensity={1.3} position={[6, -3, 3]} scale={[9, 9, 1]} color="#ffe6c4" />
      </Environment>

      <Suspense fallback={null}>
        <DrumPlane />
      </Suspense>
      <Bubbles count={18} />
      <Sparkles
        count={220}
        scale={[13, 9, 14]}
        position={[0, 0, -2]}
        size={4.5}
        speed={0.4}
        opacity={0.7}
        color="#ffffff"
      />
      <Rig progress={progress} />
    </Canvas>
  );
}
